import { defineStore } from 'pinia'
import { START_LOCATION } from 'vue-router'

import { sanitize } from '@/@core/utils/formatters'
import ability, { initialAbility } from '@/plugins/casl/ability'
import router from '@/router'
import { loginReturnPath } from '@/router/goLinks'
import { isAppPath } from '@/router/utils'
import { resetInsuranceStoresOnLogout } from '@/stores/insurance/resetOnLogout'
import { abandonInFlightUserGet, useUserStore } from '@/stores/user'
import { resetBehaviorBufferForAuthChange, resetBehaviorSession, resetCampaignArrivalsForAuthChange } from '@/utils/instrumentation'
import axios from '@axios'

// Singleton guard — prevents parallel POST /logout requests when multiple callers
// (session timer, 401 interceptor, user click) race at session expiry.
let logoutInFlight = false

// Singleton guard for tokenLogin() — the server nulls a magic-link token out
// the moment it's used, so any second POST with the same token always 422s
// even though the first request already logged the user in. Duplicate calls
// can come from the same page load (e.g. an effect firing twice) or from a
// second submission of the same token (e.g. the login page remounting after
// a slow/reloaded request, or an email link scanner pre-visiting the link).
// Concurrent calls for the same token share one in-flight request instead of
// firing a second POST.
let tokenLoginToken: string | null = null
let tokenLoginPromise: Promise<unknown> | null = null

interface LoginRequest {
  email: string
  password: string
}

export interface ResendTFAResponse {
  redacted_email: string
}

/**
 * Where to send the user after they log in again, when a logout happens before the first
 * page has loaded.
 *
 * That only happens when a session expired while the tab was closed and the user then opened
 * a URL, e.g. a product-update email link, whose `utm_campaign` would otherwise be lost.
 * Explicit and idle logouts happen after the first navigation and go to plain /login, so a
 * shared workstation doesn't hand the next person the previous user's page.
 */
function resumeAfterLogin(): string | undefined {
  if (router.currentRoute.value !== START_LOCATION)
    return undefined

  const { pathname, search } = window.location
  if (!isAppPath(pathname) || pathname === '/' || pathname.startsWith('/login'))
    return undefined

  return loginReturnPath(pathname + search)
}

export function useAuthStore() {
  // Import the router singleton directly rather than useRouter()/useRoute() — those
  // composables require an active component setup() context via Vue's inject(), and
  // useAuthStore() is also called from the global axios 401 interceptor (axios.ts),
  // which runs outside any component context. Calling useRouter() there silently
  // returns undefined (with an "inject() can only be used inside setup()" warning),
  // so router.push() later would throw. router.currentRoute is a plain reactive ref
  // on the singleton and works from anywhere.
  const user = useUserStore()

  return defineStore('auth', {
    state: () => ({
      error: '' as string | null,
      isFetching: false,
      isAuthenticated: !!localStorage.getItem('isAuthenticated'),
      email: '',
      tfa: null as any,
    }),
    getters: {
      isLoggedIn: state => state.isAuthenticated,
    },
    actions: {
      login(creds: LoginRequest) {
        this.setFetching(true)

        return new Promise((resolve, reject) => {
          // Fetch a fresh XSRF-TOKEN first, the same way resetPassword/sendSecureLoginLink do: a user
          // signing in again after their session expired otherwise posts a stale token and gets a 419.
          axios.get('/sanctum/csrf-cookie', { baseURL: `${import.meta.env.VITE_SERVER_URL}` }).then(() => {
            axios
              .post('/login', creds)
              .then(({ data }) => {
                this.tfa = data
                this.email = creds.email
                this.setFetching(false)

                // Redirect to `to` query if exist or redirect to index route
                router.push({
                  name: 'verify',
                  query: router.currentRoute.value.query,
                })

                resolve(data)
              })
              .catch(({ response }) => {
                this.setFetching(false)
                this.error = sanitize(response?.data?.message || response)
                reject(new Error(sanitize(response?.data?.message || 'Request failed')))
              })
          }).catch(({ response }) => {
            // If the csrf-cookie fetch itself fails, /login never runs — without this the outer
            // promise never settles and the sign-in button spins forever.
            this.setFetching(false)
            this.error = sanitize(response?.data?.message || response)
            reject(new Error(sanitize(response?.data?.message || 'Request failed')))
          })
        })
      },
      sendSecureLoginLink(creds: LoginRequest & { redirect_path?: string }) {
        this.setFetching(true)

        return new Promise((resolve, reject) => {
          axios.get('/sanctum/csrf-cookie', { baseURL: `${import.meta.env.VITE_SERVER_URL}` }).then(() => {
            axios
              .post('/send-secure-login-link', creds)
              .then(({ data }) => {
                // An authenticator-app user never gets a magic link — the server
                // returns the same verification_token challenge password login
                // uses, so store it the same way and skip straight to /verify.
                if (data.type === 'verification_token')
                  this.tfa = data

                this.setFetching(false)
                resolve(data)
              })
              .catch(({ response }) => {
                this.setFetching(false)
                this.error = response?.data?.message || response
                reject(new Error(response?.data?.message || 'Request failed'))
              })
          }).catch(({ response }) => {
            // If the csrf-cookie fetch itself fails, /send-secure-login-link never runs —
            // without this the outer promise never settles and the caller hangs forever.
            this.setFetching(false)
            this.error = response?.data?.message || response
            reject(new Error(response?.data?.message || 'Request failed'))
          })
        })
      },

      // A TFA-enabled user isn't authenticated yet — the server returns a
      // verification_token challenge instead of a session, same as password
      // login. Store it the same way login() does so /verify can read it.
      applyTokenLoginResponse(data: any) {
        if (data.type === 'verification_token')
          this.tfa = data
        else
          this.setAuthenticated(true)

        this.setFetching(false)
      },
      tokenLogin(token: string) {
        // Same token already has a request in flight (or just resolved and
        // hasn't been cleared yet) — return that promise instead of firing a
        // second POST that the server will reject with a used-token 422.
        if (tokenLoginToken === token && tokenLoginPromise)
          return tokenLoginPromise

        this.setFetching(true)

        tokenLoginToken = token
        tokenLoginPromise = new Promise((resolve, reject) => {
          axios.get('/sanctum/csrf-cookie', { baseURL: `${import.meta.env.VITE_SERVER_URL}` }).then(() => {
            axios
              .post('/token-login', { token })
              .then(({ data }) => {
                this.applyTokenLoginResponse(data)
                resolve(data)
              })
              .catch(({ response }) => {
                this.setFetching(false)

                // A duplicate/late request for a token that already succeeded
                // (from this store's point of view) must not clobber a real
                // login with an expired-link error — the user is already in.
                if (this.isAuthenticated && tokenLoginToken === token) {
                  resolve(null)

                  return
                }

                this.error = response?.data?.message || response
                reject(new Error(response?.data?.message || 'Request failed'))
              })
          }).catch(({ response }) => {
            // If the csrf-cookie fetch itself fails, /token-login never runs —
            // without this the outer promise never settles and login.vue's page just spins.
            this.setFetching(false)
            this.error = response?.data?.message || response
            reject(new Error(response?.data?.message || 'Request failed'))
          })
        }).finally(() => {
          if (tokenLoginToken === token) {
            tokenLoginToken = null
            tokenLoginPromise = null
          }
        })

        return tokenLoginPromise
      },
      verifyTFA(verification_code: string) {
        this.setFetching(true)

        // The authenticator-app method has its own endpoint — it accepts a live
        // TOTP code or a recovery code, neither of which the shared /verify-phone
        // flow (phone + email codes) understands.
        const request = this.tfa?.verification_type === 'app'
          ? axios.post('/verify-authenticator', {
            verification_token: this.tfa.token,
            code: verification_code,
          })
          : axios.post('/verify-phone', {
            verification_token: this.tfa.token,
            verification_code,
          })

        return new Promise((resolve, reject) => {
          request
            .then(() => {
              this.setAuthenticated(true)

              // Keep the loader up until the dashboard is actually reached: the router guard
              // awaits the user/entity fetch, and clearing it earlier leaves a dead gap.
              router.push({
                path: '/',
                query: router.currentRoute.value.query,
              }).finally(() => this.setFetching(false))
            })
            .catch(({ response }) => {
              this.setFetching(false)
              this.error = response?.data?.message || response
              reject(new Error(response?.data?.message || 'Request failed'))
            })
        })
      },
      resendPhoneTFA() {
        this.setFetching(true)

        return new Promise((resolve, reject) => {
          if (this.tfa) {
            axios
              .post('/send-phone-code', {
                verification_token: this.tfa.token,
                email: this.email,
              })
              .then(() => {
                this.setFetching(false)
                resolve({})
              })
              .catch(({ response }) => {
                this.setFetching(false)
                this.error = response?.data?.message || response
                reject(new Error(response?.data?.message || 'Request failed'))
              })
          }
        })
      },
      resendEmailTFA(): Promise<ResendTFAResponse> {
        this.setFetching(true)

        return new Promise((resolve, reject) => {
          if (this.tfa) {
            axios
              .post('/send-email-code', {
                verification_token: this.tfa.token,
                email: this.email,
              })
              .then(({ data }) => {
                this.setFetching(false)
                resolve({ redacted_email: data.redacted_email })
              })
              .catch(({ response }) => {
                this.setFetching(false)
                this.error = response?.data?.message || response
                reject(new Error(response?.data?.message || 'Request failed'))
              })
          }
        })
      },
      logout() {
        if (logoutInFlight)
          return

        logoutInFlight = true

        // Captured before the reset below. See resumeAfterLogin().
        const resumeTo = resumeAfterLogin()

        this.tfa = null
        this.setFetching(false)
        this.setAuthenticated(false)
        localStorage.removeItem('isVector')
        user.$reset()
        resetInsuranceStoresOnLogout()
        abandonInFlightUserGet()
        ability.update(initialAbility)

        axios
          .post('/logout', null)

          // eslint-disable-next-line @typescript-eslint/no-empty-function
          .catch(() => {})
          .finally(() => {
            logoutInFlight = false

            // Teardown-time resolve/navigation failures must not surface as
            // unhandled rejections — that's how Rollbar #1303/#1311 were reported,
            // since nothing awaits this push. (VH-2283)
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            router.push(resumeTo ? { name: 'login', query: { to: resumeTo } } : '/login').catch(() => {})
          })
      },
      forgotPassword(email: string) {
        // Users typically land here after their session expired (from /login or a
        // protected page), so the XSRF-TOKEN cookie is missing or stale and the POST
        // 419s with "CSRF token mismatch" (Rollbar #1281). Refresh it first, the same
        // way resetPassword/tokenLogin/sendSecureLoginLink do. A failed csrf-cookie
        // fetch rejects the same way a failed POST does, so the caller never hangs.
        return axios
          .get('/sanctum/csrf-cookie', { baseURL: `${import.meta.env.VITE_SERVER_URL}` })
          .then(() => axios.post('/forgot-password', { email }))
          .then(({ data }) => data)
          .catch(({ response }) => {
            throw new Error(response?.data?.message || 'Request failed')
          })
      },
      resetPassword(body: any) {
        this.setFetching(true)

        return new Promise((resolve, reject) => {
          axios.get('/sanctum/csrf-cookie', { baseURL: `${import.meta.env.VITE_SERVER_URL}` }).then(() => {
            axios
              .post('/reset-password', body)
              .then(data => {
                this.setFetching(false)
                resolve(data)
              })
              .catch(({ response }) => {
                this.setFetching(false)
                this.error = response?.data?.message || response
                reject(new Error(response?.data?.message || 'Request failed'))
              })
          }).catch(({ response }) => {
            // If the csrf-cookie fetch itself fails, /reset-password never runs —
            // without this the outer promise never settles and the caller hangs forever.
            this.setFetching(false)
            this.error = response?.data?.message || response
            reject(new Error(response?.data?.message || 'Request failed'))
          })
        })
      },
      register(creds: LoginRequest) {
        this.setFetching(true)

        return new Promise((resolve, reject) => {
          axios
            .post(`${import.meta.env.VITE_SERVER_URL}/register`, creds)
            .then(({ data }) => {
              this.tfa = data
              this.setFetching(false)
              resolve(data)
            })
            .catch(({ response }) => {
              this.setFetching(false)
              this.error = response?.data?.message || response
              reject(new Error(response?.data?.message || 'Request failed'))
            })
        })
      },
      setFetching(value: boolean) {
        this.isFetching = value
        if (value)
          this.error = null
      },
      setAuthenticated(value: boolean) {
        this.isAuthenticated = value

        // Rotate the behavior-tracking session on every auth transition, in
        // both directions. Logout matters as much as login: at a shared front
        // desk, keeping the id would merge the next person's funnels into the
        // previous user's session. This is the one choke point every path
        // (password, 2FA, magic link, logout) already passes through.
        //
        // Order matters. Rotating the id alone left the buffer untouched, so
        // events recorded by the outgoing user were still queued when the next
        // one signed in — and the server reads user_id from the session on the
        // ingest request, not from the event, so they would have been written
        // against the wrong person. Drain and clear before the rotation.
        resetBehaviorBufferForAuthChange()
        resetBehaviorSession()
        resetCampaignArrivalsForAuthChange()

        if (value)
          localStorage.setItem('isAuthenticated', 'true')
        else
          localStorage.removeItem('isAuthenticated')
      },
      syncAuthenticated() {
        this.isAuthenticated = !!localStorage.getItem('isAuthenticated')
      },
    },
  })()
}
